/**
 * 인증 관리 Zustand 스토어 및 훅
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { AuthStore, LoginRequest, User } from '../types/auth.types';
import { authService } from '../services/auth.service';

/**
 * 인증 스토어
 */
export const useAuth = create<AuthStore>((set, get) => ({
  // 초기 상태
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  /**
   * 로그인
   */
  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.login(credentials);

      set({
        user: response.user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : '로그인에 실패했습니다.',
      });
      throw error;
    }
  },

  /**
   * 로그아웃
   */
  logout: () => {
    // API 로그아웃 호출 (비동기로 처리하되 대기하지 않음)
    authService.logout().catch(error => {
      console.error('Logout error:', error);
    });

    // 즉시 상태 초기화
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  /**
   * 토큰 갱신
   */
  refreshAccessToken: async () => {
    const { refreshToken: storedRefreshToken } = get();

    if (!storedRefreshToken) {
      throw new Error('리프레시 토큰이 없습니다.');
    }

    try {
      const response = await authService.refreshToken(storedRefreshToken);

      set({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });
    } catch (error) {
      // 토큰 갱신 실패 시 로그아웃
      get().logout();
      throw error;
    }
  },

  /**
   * 사용자 정보 갱신
   */
  updateUser: (user: Partial<User>) => {
    const currentUser = get().user;

    if (!currentUser) {
      return;
    }

    set({
      user: {
        ...currentUser,
        ...user,
      },
    });
  },

  /**
   * 인증 상태 초기화 (앱 시작 시 호출)
   */
  initialize: () => {
    set({ isLoading: true });

    try {
      // 저장된 토큰 확인
      const { accessToken, refreshToken } = authService.getStoredTokens();
      const user = authService.getStoredUser();

      if (accessToken && user) {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        // 사용자 정보 갱신 (비동기로 처리, 실패해도 로그아웃하지 않음)
        authService
          .getCurrentUser()
          .then(updatedUser => {
            set({ user: updatedUser });
          })
          .catch(error => {
            console.warn('Failed to fetch current user (non-fatal):', error);
          });
      } else {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : '인증 상태를 확인할 수 없습니다.',
      });
    }
  },

  /**
   * 에러 초기화
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * 로딩 상태 설정
   */
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },
}));

/**
 * 인증 상태 선택자 (성능 최적화를 위한 개별 선택자)
 */
export const useAuthUser = () => useAuth(state => state.user);
export const useIsAuthenticated = () => useAuth(state => state.isAuthenticated);
export const useAuthLoading = () => useAuth(state => state.isLoading);
export const useAuthError = () => useAuth(state => state.error);

/**
 * 인증 액션 선택자 (shallow 비교로 불필요한 리렌더링 방지)
 */
export const useAuthActions = () =>
  useAuth(
    useShallow(state => ({
      login: state.login,
      logout: state.logout,
      refreshAccessToken: state.refreshAccessToken,
      updateUser: state.updateUser,
      initialize: state.initialize,
      clearError: state.clearError,
      setLoading: state.setLoading,
    }))
  );

export default useAuth;
